import React, { useState } from "react";
import Panel from "../../panels/Panel";
import ExpandButton from "../../graphs/ExpandButton";
import UsdsLineChart from "./UsdsLineChart";
import UsdsProfileChart from "./UsdsProfileChart";
import { Typography } from "@material-ui/core";
import UsdsChartLegend, {
  benchmarkLegendItems,
  landmarkLegendItems,
} from "../usdsBoxPlot/UsdsChartLegend";

/**
 * A single reach panel for the line view: a stream-mile profile when every
 * site on the reach has a distance (Cherry Creek), otherwise sites spaced
 * evenly in order. Its rows are the page's Raw Data Table, so it carries no
 * table of its own.
 */
export default function UsdsLineChartContainer({
  reach,
  statisticLabel,
  scale = "linear",
  landmarks = [],
  mouths = [],
  height = 420,
}) {
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (label) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  /* chart.js lists the series itself; only the benchmark needs adding, and
     the primary parameter's values travel on its series rows. */
  const primary = reach.series.find((s) => !s.isSecondary);
  const benchmarkItems = benchmarkLegendItems(
    primary && {
      hasBenchmark:
        primary.benchmarkHigh != null || primary.benchmarkLow != null,
      benchmarkHigh: primary.benchmarkHigh,
      benchmarkLow: primary.benchmarkLow,
      unitName: primary.unitName,
      isTwoSided: primary.benchmarkHigh != null && primary.benchmarkLow != null,
    }
  );

  const isProfile = Boolean(reach.profile?.available);
  const hideBenchmark = benchmarkItems.some((b) => hidden.has(b.label));
  const chartHeight = expanded ? Math.round(height * 1.8) : height;
  const estimated = isProfile
    ? [
        ...(reach.profile.estimatedSites ?? []),
        ...mouths.filter((m) => m.distanceEstimated).map((m) => m.label),
      ]
    : [];

  return (
    <Panel
      title={
        isProfile
          ? `${reach.reachLabel} profile by stream mile`
          : reach.reachLabel
      }
      rightHeader={
        <>
          <ExpandButton
            handleExpand={() => setExpanded((v) => !v)}
            expanded={expanded}
          />
        </>
      }
    >
      <div>
        <UsdsChartLegend
          items={[...benchmarkItems, ...landmarkLegendItems(landmarks)]}
          hidden={hidden}
          onToggle={toggle}
        />
        {isProfile ? (
          <UsdsProfileChart
            reach={reach}
            mouths={mouths}
            landmarks={landmarks}
            statisticLabel={statisticLabel}
            hideBenchmark={hideBenchmark}
            scale={scale}
            height={chartHeight}
          />
        ) : (
          <UsdsLineChart
            reach={reach}
            landmarks={landmarks}
            statisticLabel={statisticLabel}
            hideBenchmark={hideBenchmark}
            scale={scale}
            height={chartHeight}
          />
        )}
        {estimated.length > 0 && (
          <Typography variant="caption" color="textSecondary" component="p">
            Positions estimated from latitude until a stream distance is
            measured, drawn hollow: {estimated.join(", ")}.
          </Typography>
        )}
      </div>
    </Panel>
  );
}
